/**
 * React Query Hooks - Unified API request Hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/api-client';
import { queryKeys } from '../lib/query-client';

// ==================== Auth Hooks ====================

export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: () => apiClient.get('/me'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// ==================== User Hooks ====================

export function useUserDirectory() {
  return useQuery({
    queryKey: queryKeys.users.directory,
    queryFn: () => apiClient.get('/users/directory'),
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useUserSearch(searchQuery, options = {}) {
  return useQuery({
    queryKey: queryKeys.users.search(searchQuery),
    queryFn: () => apiClient.get('/users/directory/search', { 
      params: { q: searchQuery, limit: 20 } 
    }),
    enabled: !!searchQuery && searchQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useUserPhoto(userId) {
  return useQuery({
    queryKey: queryKeys.users.photo(userId),
    queryFn: () => apiClient.get(`/users/${userId}/photo`),
    enabled: !!userId,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useUserDetail(userId) {
  return useQuery({
    queryKey: queryKeys.users.detail(userId),
    queryFn: () => apiClient.get(`/users/${userId}`),
    enabled: !!userId,
  });
}

export function useManagerChain(userId, levels = 5) {
  return useQuery({
    queryKey: queryKeys.users.managerChain(userId),
    queryFn: () => apiClient.get(`/users/${userId}/manager-chain`, { 
      params: { levels } 
    }),
    enabled: !!userId,
  });
}

// ==================== Form Hooks ====================

export function useFormTypes(language = 'en') {
  return useQuery({
    queryKey: queryKeys.forms.types(language),
    queryFn: () => apiClient.get('/forms/types', {
      params: { lang: language }
    }),
    staleTime: 30 * 60 * 1000, // 30 mins
  });
}

export function useFormConfig(formId, language = 'en') {
  return useQuery({
    queryKey: queryKeys.forms.config(formId, language),
    queryFn: () => apiClient.get(`/forms/config/${formId}`, { 
      params: { lang: language } 
    }),
    enabled: (!!formId && formId !== 'null' && formId !== 'undefined'),
    staleTime: 30 * 60 * 1000,
  });
}

export function useFormDetail(applicationId) {
  return useQuery({
    queryKey: queryKeys.forms.detail(applicationId),
    queryFn: () => apiClient.get(`/forms/${applicationId}`),
    enabled: !!applicationId,
  });
}

export function usePendingApprovals() {
  return useQuery({
    queryKey: queryKeys.forms.pending,
    queryFn: () => apiClient.get('/forms/pending/approvals'),
  });
}

export function useFormSearch(filters) {
  return useQuery({
    queryKey: queryKeys.forms.list(filters),
    queryFn: () => apiClient.get('/forms/search/my', { params: filters }),
    enabled: !!filters,
  });
}

// Form Mutations
export function useCreateForm() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (formData) => apiClient.post('/forms', formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.all });
    },
  });
}

export function useUpdateForm() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ applicationId, data }) => 
      apiClient.put(`/forms/${applicationId}`, data),
    onSuccess: (_, { applicationId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.forms.detail(applicationId) 
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.all });
    },
  });
}

export function useSubmitForm() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (applicationId) => 
      apiClient.post(`/forms/${applicationId}/submit`),
    onSuccess: (_, applicationId) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.forms.detail(applicationId) 
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.all });
    },
  });
}

export function useApproveForm() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ applicationId, action, comment }) => 
      apiClient.post(`/forms/${applicationId}/approve`, { action, comment }),
    onSuccess: (_, { applicationId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.forms.detail(applicationId) 
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.pending });
    },
  });
}

export function useDeleteForm() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (applicationId) => apiClient.delete(`/forms/${applicationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.all });
    },
  });
}

// ==================== Admin Actions Hooks ====================

export function useAdminActions() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ applicationId, action, comment, targetStepId, newStatus }) => 
      apiClient.post(`/forms/${applicationId}/admin`, { 
        action, 
        comment,
        targetStepId,
        newStatus 
      }),
    onSuccess: (_, { applicationId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.forms.detail(applicationId) 
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.pending });
    },
  });
}

export function useUpdateApprovalFlow() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ applicationId, approvalFlow }) => 
      apiClient.put(`/forms/${applicationId}/approval-flow`, { approvalFlow }),
    onSuccess: (_, { applicationId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.forms.detail(applicationId) 
      });
    },
  });
}

// ==================== Config Hooks ====================

export function useConfigFile(path) {
  return useQuery({
    queryKey: queryKeys.config.file(path),
    queryFn: () => apiClient.get('/config/file', { params: { path } }),
    enabled: !!path,
  });
}

export function useConfigList() {
  return useQuery({
    queryKey: queryKeys.config.all,
    queryFn: () => apiClient.get('/config/list'),
  });
}

export function useConfigBackups() {
  return useQuery({
    queryKey: ['config', 'backups'],
    queryFn: () => apiClient.get('/config/backups'),
  });
}

export function useSaveConfigFile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ path, content }) => 
      apiClient.put(`/config/file?path=${encodeURIComponent(path)}`, { content }),
    onSuccess: (_, { path }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.config.file(path) });
      queryClient.invalidateQueries({ queryKey: queryKeys.config.all });
    },
  });
}

export function useValidateConfig() {
  return useMutation({
    mutationFn: (content) => apiClient.post('/config/validate', { content }),
  });
}

export function useRestoreBackup() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (backupPath) => apiClient.post('/config/restore', { backupPath }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.config.all });
      queryClient.invalidateQueries({ queryKey: ['config', 'backups'] });
    },
  });
}

// ==================== Debug Hooks ====================

export function useDebugStatus() {
  return useQuery({
    queryKey: ['debug', 'status'],
    queryFn: () => apiClient.get('/debug/status'),
  });
}

export function useCacheStats() {
  return useQuery({
    queryKey: ['users', 'cache', 'stats'],
    queryFn: () => apiClient.get('/users/cache/stats'),
  });
}

export function useApproversConfig() {
  return useQuery({
    queryKey: queryKeys.debug.approversConfig,
    queryFn: () => apiClient.get('/debug/approvers-config'),
  });
}

export function useSimulateApprovalFlow() {
  return useMutation({
    mutationFn: (data) => apiClient.post('/debug/simulate-approval', data),
  });
}

export function useSimulateApproval() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ applicationId, action, comment }) => 
      apiClient.post(`/forms/${applicationId}/approve`, { action, comment }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.pending });
    },
  });
}

// ==================== Form Calculate Hook ====================

export function useCalculateForm() {
  return useMutation({
    mutationFn: ({ formId, formData, fieldId, calculateFlow }) => 
      apiClient.post('/forms/calculate', { formId, formData, fieldId, calculateFlow }),
  });
}

// ==================== Notification Hooks ====================

export function useNotificationStatus() {
  return useQuery({
    queryKey: ['notification', 'status'],
    queryFn: () => apiClient.get('/forms/notification-status'),
    staleTime: 60000, // 1 minute
  });
}

export function useTestNotification() {
  return useMutation({
    mutationFn: ({ type, recipient, templateType, applicationId }) => 
      apiClient.post('/forms/test-notification', { type, recipient, templateType, applicationId }),
  });
}

export function useSendNotification() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ applicationId, type, recipients, subject, message }) => 
      apiClient.post(`/forms/${applicationId}/notify`, { type, recipients, subject, message }),
    onSuccess: (_, { applicationId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.forms.detail(applicationId) 
      });
    },
  });
}

// ==================== Reminder Hooks ====================

export function useReminders(filters) {
  return useQuery({
    queryKey: queryKeys.reminders.list(filters),
    queryFn: () => apiClient.get('/reminders', { params: filters }),
    enabled: !!filters,
  });
}

export function useReminder(reminderId) {
  return useQuery({
    queryKey: queryKeys.reminders.detail(reminderId),
    queryFn: () => apiClient.get(`/reminders/${reminderId}`),
    enabled: !!reminderId,
  });
}

export function useUpcomingReminders(days = 7) {
  return useQuery({
    queryKey: queryKeys.reminders.upcoming,
    queryFn: () => apiClient.get('/reminders/upcoming', { params: { days } }),
  });
}

export function useReminderCategories() {
  return useQuery({
    queryKey: queryKeys.reminders.categories,
    queryFn: () => apiClient.get('/reminders/meta/categories'),
    staleTime: 10 * 60 * 1000, // 10 mins
  });
}

export function useReminderTags() {
  return useQuery({
    queryKey: queryKeys.reminders.tags,
    queryFn: () => apiClient.get('/reminders/meta/tags'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateReminder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (reminderData) => apiClient.post('/reminders', reminderData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reminders.all });
    },
  });
}

export function useUpdateReminder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reminderId, data }) => 
      apiClient.put(`/reminders/${reminderId}`, data),
    onSuccess: (_, { reminderId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.reminders.detail(reminderId) 
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.reminders.all });
    },
  });
}

export function useDeleteReminder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (reminderId) => apiClient.delete(`/reminders/${reminderId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reminders.all });
    },
  });
}

export function useUpdateReminderStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reminderId, status }) => 
      apiClient.put(`/reminders/${reminderId}`, { status }),
    onSuccess: (_, { reminderId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.reminders.detail(reminderId) 
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.reminders.all });
    },
  });
}

export function useAddCollaborators() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reminderId, collaborators }) => 
      apiClient.post(`/reminders/${reminderId}/collaborators`, { collaborators }),
    onSuccess: (_, { reminderId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.reminders.detail(reminderId) 
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.reminders.frequentContacts });
    },
  });
}

export function useRemoveCollaborator() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reminderId, userId }) => 
      apiClient.delete(`/reminders/${reminderId}/collaborators/${userId}`),
    onSuccess: (_, { reminderId }) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.reminders.detail(reminderId) 
      });
    },
  });
}
